import { esc } from "../lib/escape";
import { clearSession } from "../auth/session";
import { deleteAccountData } from "../api/mobile";
import { showToast } from "../components/ui";
import type { MobileAccount } from "../types";

function subscribeUrl(email?: string): string {
  const q = new URLSearchParams({ upgrade: "1" });
  if (email) q.set("email", email);
  return `https://frogswork.com/account/subscribe.html?${q}`;
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const end = Date.parse(iso);
  if (!Number.isFinite(end)) return null;
  const ms = end - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function isCancelledSoftLock(account: MobileAccount): boolean {
  return (
    !account.active &&
    Boolean(account.delete_scheduled_at || account.status === "canceled")
  );
}

export function renderUpgrade(
  root: HTMLElement,
  account: MobileAccount,
  onSignOut: () => void,
  onDeletedData?: () => void
) {
  const cancelled = isCancelledSoftLock(account);
  const daysLeft = daysUntil(account.delete_scheduled_at);
  const deleteDate = formatDate(account.delete_scheduled_at);
  const retained = account.cloud_data_retained !== false && (daysLeft == null || daysLeft > 0);

  const title = cancelled ? "Sorry to see you go" : "Subscription required";
  const body = cancelled
    ? retained
      ? `<p class="hint">Your subscription has ended. Resubscribe to keep using FrogsWork with your existing data.</p>
         <p class="hint"><strong>Your account data will be automatically deleted in ${
           daysLeft == null ? "a few" : daysLeft
         } day${daysLeft === 1 ? "" : "s"}${
           deleteDate ? ` (${esc(deleteDate)})` : ""
         }.</strong> You can delete it now if you prefer.</p>`
      : `<p class="hint">Your subscription has ended and your invoice data has been deleted. Resubscribe to start fresh, or delete your account from Settings after you subscribe again.</p>`
    : `<p class="hint">Your FrogsWork subscription is inactive. Subscribe or renew to use this app.</p>`;

  root.innerHTML = `
    <div class="gate-screen">
      <div class="gate-brand gate-brand-compact">
        <img class="gate-logo gate-logo-sm" src="/icons/splash-logo.png" alt="FrogsWork" width="64" height="64">
        <p class="gate-tagline">Sales invoicing for Australian sole traders</p>
      </div>
      <section class="panel upgrade-panel gate-card">
        <h2>${title}</h2>
        <p class="upgrade-signed-in">Signed in as ${esc(account.email)}</p>
        ${body}
        <div class="btn-row stacked">
          <a class="btn primary btn-block" href="${esc(subscribeUrl(account.email))}">${
            cancelled ? "Resubscribe" : "Subscribe"
          }</a>
          ${
            account.portal_url
              ? `<a class="btn secondary btn-block" href="${esc(account.portal_url)}" target="_blank" rel="noopener">Manage subscription</a>`
              : ""
          }
          ${
            cancelled && retained
              ? `<button type="button" class="btn danger btn-block" id="upgrade-delete-data">Delete my data now</button>`
              : ""
          }
        </div>
        <button type="button" id="upgrade-sign-out" class="btn ghost">Use a different account</button>
      </section>
      <footer class="gate-footer">
        <a href="https://korraone.com" target="_blank" rel="noopener">KorraOne</a>
        ·
        <a href="https://frogswork.com" target="_blank" rel="noopener">frogswork.com</a>
      </footer>
    </div>`;

  root.querySelector("#upgrade-sign-out")?.addEventListener("click", () => {
    clearSession();
    onSignOut();
  });

  root.querySelector("#upgrade-delete-data")?.addEventListener("click", () => {
    void confirmDeleteDataNow(root, account, onSignOut, onDeletedData);
  });
}

async function confirmDeleteDataNow(
  root: HTMLElement,
  account: MobileAccount,
  onSignOut: () => void,
  onDeletedData?: () => void
) {
  const phrase = "DELETE DATA";
  const overlay = document.createElement("div");
  overlay.className = "sheet-overlay";
  overlay.innerHTML = `
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Delete my data now">
      <div class="sheet-handle" aria-hidden="true"></div>
      <h3 class="sheet-title">Delete my data now</h3>
      <div class="sheet-body">
        <p class="hint">Permanently erases your businesses, customers, invoices, quotes, and PDFs. Your login stays so you can resubscribe later.</p>
        <p class="hint"><strong>This cannot be undone.</strong></p>
        <div class="field" style="margin-top:0.75rem">
          <label for="upgrade-wipe-confirm">Type ${esc(phrase)} to confirm</label>
          <input id="upgrade-wipe-confirm" type="text" autocomplete="off" />
        </div>
      </div>
      <div class="sheet-actions">
        <button type="button" class="btn secondary" data-action="cancel">Cancel</button>
        <button type="button" class="btn danger" data-action="confirm">Delete data</button>
      </div>
    </div>`;
  const finish = () => {
    document.body.classList.remove("sheet-open");
    overlay.remove();
  };
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) finish();
  });
  overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", finish);
  overlay.querySelector('[data-action="confirm"]')?.addEventListener("click", async () => {
    const input = overlay.querySelector("#upgrade-wipe-confirm") as HTMLInputElement | null;
    if ((input?.value || "").trim() !== phrase) {
      showToast(`Type ${phrase} exactly to confirm.`, "error");
      return;
    }
    try {
      await deleteAccountData();
      finish();
      showToast("All account data deleted.", "success");
      const next = { ...account, cloud_data_retained: false, delete_scheduled_at: account.delete_scheduled_at };
      if (onDeletedData) onDeletedData();
      renderUpgrade(root, next, onSignOut, onDeletedData);
    } catch (ex) {
      showToast(ex instanceof Error ? ex.message : "Could not delete data.", "error");
    }
  });
  document.body.classList.add("sheet-open");
  document.body.appendChild(overlay);
}
